import React, { useCallback, useEffect, useState } from 'react';
import { v2StackAPI } from '../services/api';
import { getPositionDisplayStyles } from '../utils/stack';
import { STACK_COLORS, STACK_TEXT_COLORS } from '../utils/stackColors';
import { BUTTON_COLORS } from '../utils/buttonColors';

const BotStackSelector = ({ 
  isOpen, 
  onClose, 
  stackId,
  onSelect 
}) => {
  const [botStacksData, setBotStacksData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(false);

  const loadBotStacks = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await v2StackAPI.getStackBotStacks(stackId);
      setBotStacksData(response.data);
      setCurrentIndex(0);
    } catch (err) {
      setError('Failed to load bot stacks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [stackId]);

  useEffect(() => {
    if (isOpen && stackId) {
      loadBotStacks();
    }
  }, [isOpen, stackId, loadBotStacks]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (botStacksData && currentIndex < botStacksData.bot_stacks.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSelect = async () => {
    if (!botStacksData || !botStacksData.bot_stacks[currentIndex]) return;
    
    try {
      setSelecting(true);
      setError('');
      const botStackId = botStacksData.bot_stacks[currentIndex].id;
      await v2StackAPI.completeBotStack(stackId, botStackId);
      
      // Call onSelect which should navigate to the stack view
      if (onSelect) {
        onSelect(stackId);
      }
    } catch (err) {
      setError('Failed to complete bot stack');
      console.error(err);
    } finally {
      setSelecting(false);
    }
  };

  const handleClose = () => {
    setBotStacksData(null);
    setCurrentIndex(0);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const currentBotStack = botStacksData?.bot_stacks[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = botStacksData && currentIndex === botStacksData.bot_stacks.length - 1;
  const hasBotStacks = botStacksData && botStacksData.bot_stacks.length > 0;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--overlay)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 'env(safe-area-inset-top, 16px) 16px 16px'
    }}>
      <div style={{
        backgroundColor: 'var(--color-surface)',
        color: 'var(--color-text)',
        padding: '20px 32px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 4px 8px var(--color-shadow)',
        maxHeight: '90dvh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Select Bot Stack</h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-border)';
              e.target.style.color = 'var(--color-text)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--color-text-muted)';
            }}
            title="Close dialog (Esc)"
          >
            ×
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading bot stacks...
          </div>
        )}

        {error && (
          <div className="error-message" style={{ marginBottom: '12px' }}>
            {error}
          </div>
        )}

        {!loading && hasBotStacks && currentBotStack && (
          <>
            {/* Stack Name */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-text)' }}>
                {currentBotStack.name || 'Untitled Stack'}
              </h4>
              <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                {currentIndex + 1} of {botStacksData.bot_stacks.length}
              </div>
            </div>

            {/* Positions Display */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginBottom: '20px' }}>
              {currentBotStack.positions.map((position, idx) => {
                const colorIndex = idx % STACK_COLORS.length;
                return (
                  <div key={position.position_id} style={getPositionDisplayStyles(
                    position.name,
                    STACK_COLORS[colorIndex],
                    STACK_TEXT_COLORS[colorIndex]
                  )}>
                    <span>{position.name}</span>
                  </div>
                );
              })}
            </div>

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={handleClose}
                className="btn"
                style={{ 
                  backgroundColor: BUTTON_COLORS.PRIMARY, 
                  color: '#fff',
                  flex: '0 0 auto'
                }}
              >
                Back
              </button>

              <div style={{ display: 'flex', gap: '10px', flex: '1', justifyContent: 'center' }}>
                <button
                  onClick={handlePrevious}
                  disabled={isFirst}
                  className="btn"
                  style={{ 
                    backgroundColor: isFirst ? BUTTON_COLORS.DISABLED : '#6f42c1', 
                    color: '#fff',
                    opacity: isFirst ? 0.5 : 1,
                    cursor: isFirst ? 'not-allowed' : 'pointer'
                  }}
                >
                  &lt;&lt;
                </button>
                <button
                  onClick={handleNext}
                  disabled={isLast}
                  className="btn"
                  style={{ 
                    backgroundColor: isLast ? BUTTON_COLORS.DISABLED : '#6f42c1', 
                    color: '#fff',
                    opacity: isLast ? 0.5 : 1,
                    cursor: isLast ? 'not-allowed' : 'pointer'
                  }}
                >
                  &gt;&gt;
                </button>
              </div>

              <button
                onClick={handleSelect}
                disabled={selecting}
                className="btn"
                style={{ 
                  backgroundColor: BUTTON_COLORS.SUCCESS, 
                  color: '#fff',
                  flex: '0 0 auto'
                }}
              >
                {selecting ? 'Selecting...' : 'Select'}
              </button>
            </div>
          </>
        )}

        {!loading && !hasBotStacks && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-muted)' }}>
            No bot stacks available
          </div>
        )}
      </div>
    </div>
  );
};

export default BotStackSelector;

